import { useStyle } from "./style";
import broadbandPlan from "@static/img/broadband/broadbandPlan.png";
import broadbandPlanCrop from "@static/img/home/broadbandPlanDrawer.png";
import broadbandOfferNotAvailable from "@static/img/broadband/broadbandOfferNotAvailableLarge.png";
import broadbandOfferNotAvailableCrop from "@static/img/home/broadbandOfferNotAvailable.png";
import broadbandOfferAlreadyHave from "@static/img/broadband/broadbandOfferAlreadyHaveLarge.png";
import broadbandOfferAlreadyHaveCrop from "@static/img/home/broadbandOfferAlreadyHave.png";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { useHomeBroadbandResult } from "@features/home/broadband/useHomeBroadbandResult";
import { HomeBroadbandOffer } from "@features/home/common/HomeBroadbandOffer/HomeBroadbandOffer";
import { HomeBroadbandEmail } from "@features/home/common/HomeBroadbandEmail/HomeBroadbandEmail";
import { HomeBroadbandAlreadyHave } from "@features/home/common/HomeBroadbandAlreadyHave/HomeBroadbandAlreadyHave";
import {
    getIsBroadbandAlreadyHaveDisplayed,
    getIsBroadbandNotAvailableDisplayed,
    getIsBroadbandOfferDisplayed,
} from "@features/home/utils/broadbandDisplayed";
import { Box, Grid, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GetMobileLink } from "@features/home/broadband/GetMobileLink/GetMobileLink";
import { BroadbandAddon } from "@common/BroadbandAddon/BroadbandAddon";
import { useDeviceType } from "@hooks/useDeviceType";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { BUTTON_IDS } from "@features/dataLayer/buttonIds";

export const HomeBroadbandResult = () => {
    const { t } = useTranslation("home");
    const { isMobile, isMobileOrTablet } = useDeviceType();
    const { classes } = useStyle({ isMobileOrTablet, isMobile });
    const {
        broadbandOffer,
        hasBroadbandOfferInCart,
        error,
        isLoading,
        retry,
        storedPrequal,
        resetStoredPrequal,
        handleAddBroadbandOffer,
        addBroadbandOfferState,
        handleChangeAddress,
        goToLogin,
        offerAddonList,
        broadbandCartOffer,
    } = useHomeBroadbandResult();

    return (
        <HandleErrorAndLoading error={error} isHomePage isLoading={isLoading} retryFetch={retry}>
            {broadbandOffer && storedPrequal && (
                <Box display="flex" flexDirection="column" justifyContent="space-between" paddingTop={isMobile ? 0 : 7}>
                    <div className={classes.containerRow}>
                        {getIsBroadbandOfferDisplayed(storedPrequal) && (
                            <div className={classes.broadbandOfferContainer}>
                                <Grid className={classes.broadbandOffer} container>
                                    <Grid item md={6} xs={12}>
                                        <img
                                            alt={t("broadbandPlan")}
                                            className={classes.imageBroadband}
                                            src={isMobile ? broadbandPlanCrop : broadbandPlan}
                                        />
                                    </Grid>
                                    <Grid item md={6} paddingTop={isMobile ? 3 : 0} xs={12}>
                                        <HomeBroadbandOffer
                                            addBroadbandOfferState={addBroadbandOfferState}
                                            handleAddBroadbandOffer={handleAddBroadbandOffer}
                                            handleChangeAddress={handleChangeAddress}
                                            hasBroadbandOfferInCart={hasBroadbandOfferInCart}
                                            offer={broadbandOffer}
                                            prequal={storedPrequal}
                                        />
                                    </Grid>
                                </Grid>
                                {broadbandCartOffer &&
                                    offerAddonList?.map((addon) => (
                                        <Box key={addon.code} marginBottom={7} width="100%">
                                            <Box paddingInline={2}>
                                                <BroadbandAddon
                                                    addon={addon}
                                                    broadbandCartOffer={broadbandCartOffer}
                                                    isCompactLayout={isMobile}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                            </div>
                        )}
                        {getIsBroadbandNotAvailableDisplayed(storedPrequal) && (
                            <Grid className={classes.broadbandOffer} container>
                                <Grid item md={6} paddingTop={isMobile ? 2 : 0} xs={12}>
                                    <img
                                        alt="Broadband not available"
                                        className={classes.image}
                                        src={isMobile ? broadbandOfferNotAvailableCrop : broadbandOfferNotAvailable}
                                    />
                                </Grid>
                                <Grid item md={6} paddingTop={isMobile ? 3 : 0} xs={12}>
                                    <Stack alignItems="center" flexDirection="column" gap={4}>
                                        <HomeBroadbandEmail
                                            handleChangeAddress={resetStoredPrequal}
                                            isWrapped={isMobileOrTablet}
                                            persistBroadbandEmailSent
                                            prequal={storedPrequal}
                                        />
                                        <Box marginBottom={5}>
                                            <GetMobileLink />
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>
                        )}
                        {getIsBroadbandAlreadyHaveDisplayed(storedPrequal) && (
                            <Grid className={classes.broadbandOffer} container>
                                <Grid item md={6} paddingTop={isMobile ? 2 : 0} xs={12}>
                                    <img
                                        alt="Already have broadband"
                                        className={classes.image}
                                        src={isMobile ? broadbandOfferAlreadyHaveCrop : broadbandOfferAlreadyHave}
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <Stack alignItems="center" flexDirection="column" gap={4}>
                                        <HomeBroadbandAlreadyHave handleChangeAddress={resetStoredPrequal} />
                                        <Box marginBottom={5}>
                                            <GetMobileLink />
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>
                        )}
                    </div>
                    {hasBroadbandOfferInCart && (
                        <div className={classes.footer}>
                            <div className={classes.buttonContainer}>
                                <LabeledButton
                                    className={classes.button}
                                    id={BUTTON_IDS.BROADBAND_FLOW.STICKY.CONTINUE_TO_BASKET}
                                    labelText={t("goToBasket")}
                                    onClick={goToLogin}
                                >
                                    {t("goToBasket")}
                                </LabeledButton>
                            </div>
                        </div>
                    )}
                </Box>
            )}
        </HandleErrorAndLoading>
    );
};
